// Anlagen aus dem Deal im Vertragsstudio (seit 26.10.2026, Wunsch des Nutzers):
// Bausteine mit art 'anlage' stehen getrennt von den optionalen Paragraphen.
// Hat ein Anlagen-Baustein Bitrix-Felder, zeigt das Studio deren Dateien; der
// Bearbeiter waehlt Dateien und Seiten. Zum Server gehen nur Verweise
// (type 'bitrix-file'), geladen wird dort (api/_lib/dealAnlagen.js).
//
// Auswahl-Form: { [bausteinId]: { [dateiSchluessel]: { seiten: null | number[] } } }
// seiten null = alle Seiten.

const liste = (wert) => (Array.isArray(wert) ? wert : []);

export function istAnlagenBaustein(baustein) {
  return baustein?.art === 'anlage';
}

/** Teilt die Bausteine der Bibliothek in Anlagen und Paragraphen, Reihenfolge bleibt. */
export function teileBausteine(bausteine) {
  const alle = liste(bausteine);
  return {
    anlagen: alle.filter(istAnlagenBaustein),
    paragraphen: alle.filter((b) => !istAnlagenBaustein(b)),
  };
}

/** Titel ohne Vertragszusatz und ohne vorangestelltes „Anlage“. */
export function anzeigeTitel(baustein) {
  return String(baustein?.title || '')
    .replace(/\s+[-–]\s+Projektrechte\s*$/i, '')
    .replace(/^Anlage\s+/i, '')
    .trim() || 'Anlage';
}

export function dateiSchluessel(feld, dateiId) {
  return `${feld}:${dateiId}`;
}

/** Alle Bitrix-Felder der Anlagen-Bausteine, je einmal. */
export function felderDerAnlagen(anlagen) {
  return [...new Set(liste(anlagen).flatMap((b) => liste(b.bitrix_felder)))];
}

/** Dateien eines Bausteins aus der Antwort von deal-anlagen-liste: [{ feld, feldName, ...datei }]. */
export function dateienFuerBaustein(baustein, dealDateien) {
  const felder = liste(baustein?.bitrix_felder);
  return liste(dealDateien?.felder)
    .filter((f) => felder.includes(f.feld))
    .flatMap((f) => liste(f.dateien).map((d) => ({ ...d, feld: f.feld, feldName: f.name })));
}

/**
 * Nummern im Anlagenverzeichnis: die gewaehlten Anlagen in Reihenfolge der
 * Bibliothek (so setzt sie der Server ein), danach die festen Anlagen der Vorlage.
 */
export function anlagenNummern(anlagen, gewaehlteIds) {
  const gewaehlt = new Set(liste(gewaehlteIds));
  const nummern = {};
  let n = 0;
  liste(anlagen).forEach((b) => {
    if (gewaehlt.has(b.id)) {
      n += 1;
      nummern[b.id] = n;
    }
  });
  return nummern;
}

/**
 * Standardauswahl nach dem Laden der Deal-Dateien: Standard-Anlagen mit
 * mindestens einer Datei werden gewaehlt, mit allen Dateien und allen Seiten.
 * Anlagen ohne Datei im Deal bleiben aus - sonst nennte der Vertrag eine
 * Anlage, die nicht beiliegt.
 */
export function standardAuswahl(anlagen, dealDateien) {
  const ids = [];
  const auswahl = {};
  liste(anlagen).forEach((b) => {
    if (b.standard !== true) return;
    const dateien = dateienFuerBaustein(b, dealDateien);
    if (dateien.length === 0) return;
    ids.push(b.id);
    auswahl[b.id] = Object.fromEntries(dateien.map((d) => [dateiSchluessel(d.feld, d.id), { seiten: null }]));
  });
  return { ids, auswahl };
}

/**
 * Die Anlagen-Verweise fuer den Server. Je Datei ein Eintrag; die zweite und
 * jede weitere Datei derselben Anlage bekommt kein eigenes Trennblatt.
 */
export function anlagenVerweise({ anlagen, gewaehlteIds, auswahl, dealDateien }) {
  const nummern = anlagenNummern(anlagen, gewaehlteIds);
  const verweise = [];
  liste(anlagen).forEach((b) => {
    if (!nummern[b.id]) return;
    const gewaehlteDateien = auswahl?.[b.id] || {};
    let erste = true;
    dateienFuerBaustein(b, dealDateien).forEach((d) => {
      const wahl = gewaehlteDateien[dateiSchluessel(d.feld, d.id)];
      if (!wahl) return;
      const seiten = Array.isArray(wahl.seiten) ? wahl.seiten : null;
      if (seiten && seiten.length === 0) return;
      verweise.push({
        type: 'bitrix-file',
        key: `${b.id}-${d.feld}-${d.id}`,
        label: anzeigeTitel(b),
        anlageNumber: nummern[b.id],
        field: d.feld,
        fileId: String(d.id),
        filename: d.name || '',
        pages: seiten,
        fortsetzung: !erste,
      });
      erste = false;
    });
  });
  return verweise;
}

/** Wie viele Dateien je Anlagennummer aus Bitrix kommen (fuer die PDF-Anlagen-Zeilen). */
export function bitrixDateienJeNummer(verweise) {
  const zaehler = {};
  liste(verweise).forEach((v) => {
    zaehler[v.anlageNumber] = (zaehler[v.anlageNumber] || 0) + 1;
  });
  return zaehler;
}

/** „alle Seiten“, „Seite 2“, „Seiten 1–3, 5“. */
export function seitenText(seiten, gesamt) {
  if (!Array.isArray(seiten)) return gesamt ? `alle ${gesamt} Seiten` : 'alle Seiten';
  if (seiten.length === 0) return 'keine Seite';
  const sortiert = [...new Set(seiten)].sort((a, b) => a - b);
  if (gesamt && sortiert.length === gesamt) return `alle ${gesamt} Seiten`;
  const teile = [];
  let start = sortiert[0];
  let vorige = sortiert[0];
  sortiert.slice(1).concat([null]).forEach((n) => {
    if (n === vorige + 1) {
      vorige = n;
      return;
    }
    teile.push(start === vorige ? `${start}` : `${start}–${vorige}`);
    start = n;
    vorige = n;
  });
  return `${sortiert.length === 1 ? 'Seite' : 'Seiten'} ${teile.join(', ')}`;
}

/** Seitenwahl aus einer Menge; alle gewaehlt -> null (alle Seiten). */
export function seitenAusMenge(menge, gesamt) {
  const seiten = [...menge].filter((n) => Number.isInteger(n) && n >= 1).sort((a, b) => a - b);
  if (gesamt && seiten.length === gesamt) return null;
  return seiten;
}

export function dateiGroesseText(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}
